import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signIn, resetPassword } from '../lib/auth';
import Logo from '../components/Logo';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [info, setInfo]         = useState('');
  const [busy, setBusy]         = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  async function onSubmit(e) {
    e.preventDefault();
    setError(''); setInfo(''); setBusy(true);
    try {
      await signIn({ email, password });
      const dest = loc.state?.from?.pathname || '/';
      nav(dest, { replace: true });
    } catch (err) {
      setError(prettyError(err));
    } finally { setBusy(false); }
  }

  async function onReset() {
    if (!email) { setError('Enter your email first.'); return; }
    setError(''); setInfo('');
    try {
      await resetPassword(email);
      setInfo('Password reset email sent — check your inbox.');
    } catch (err) { setError(prettyError(err)); }
  }

  return (
    <AuthShell title="Sign in to the B1S Sales Hub">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input type="email" className="input" value={email} onChange={e => setEmail(e.target.value)} required />
        </div>
        <div>
          <label className="label">Password</label>
          <input type="password" className="input" value={password} onChange={e => setPassword(e.target.value)} required />
        </div>
        {error && <div className="text-red-700 text-sm">{error}</div>}
        {info  && <div className="text-emerald-700 text-sm">{info}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <div className="flex items-center justify-between text-sm">
          <button type="button" onClick={onReset} className="text-brand-600 hover:underline">Forgot password?</button>
          <Link to="/signup" className="text-brand-600 hover:underline">Create account</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ink-900 via-ink-700 to-brand-700 p-4">
      <div className="card p-6 w-full max-w-md">
        <div className="text-center mb-6 flex flex-col items-center">
          <Logo />
          <h1 className="mt-4 text-lg font-semibold text-ink-900">{title}</h1>
          <p className="mt-1 text-xs text-slate-500 italic">
            Delivering business value with professional simplicity through proven technical expertise.
          </p>
        </div>
        {children}
      </div>
    </div>
  );
}

// Supabase error messages are plain strings in err.message.
// Map the common ones to friendly copy.
function prettyError(err) {
  const msg = (err?.message || '').toLowerCase();
  if (msg.includes('invalid login credentials') || msg.includes('invalid email or password'))
    return 'Email or password is incorrect.';
  if (msg.includes('email not confirmed'))
    return 'Please confirm your email address before signing in.';
  if (msg.includes('too many requests'))
    return 'Too many attempts. Try again in a minute.';
  if (msg.includes('network') || msg.includes('fetch'))
    return 'Network error — check your connection.';
  return err?.message || 'Something went wrong.';
}
